import { ArpMedia } from '@arpeggi/shared'
import Downshift from 'downshift'
import { useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'
import { useArpMedias } from '../../../queries/arpMedia'
import Avatar from '../../core/Avatar'
import { Box, IconButton } from '../../core/Components'
import { FieldErrorLabel } from '../../core/FieldErrorLabel'
import { FieldLabel } from '../../core/FieldLabel'
import { Flex } from '../../core/Flex'
import { CloseIcon } from '../../core/icons'
import Text from '../../core/Text/Text'
import { TextInput } from '../../core/TextInput'
import { StyledMenuSelect, StyledMenuSelectItem } from '../../internal/components/UserSelect'
import { Badge } from './common'

interface Props {
  onChange: (arpMedia?: ArpMedia) => void
  defaultArpMedia?: ArpMedia
  errorMessage?: string
}

export const ArpMediaSelect: React.FC<Props> = ({ onChange, defaultArpMedia, errorMessage }) => {
  const [search, setSearch] = useState('')
  const [debouncedSearch] = useDebounce(search, 250)
  const arpMediasQuery = useArpMedias({ perPage: 20, search: debouncedSearch })

  const items = useMemo(
    () => arpMediasQuery.data?.pages.flatMap((page) => page.data.map((arpMedia) => arpMedia)) || [],
    [arpMediasQuery.data?.pages]
  )

  return (
    <Downshift
      initialSelectedItem={defaultArpMedia}
      itemToString={(item) => (item ? item.metadata.title : '')}
      onInputValueChange={(inputValue) => setSearch(inputValue)}
      onChange={(arpMedia) => onChange(arpMedia)}
    >
      {({
        isOpen,
        getLabelProps,
        getMenuProps,
        getInputProps,
        highlightedIndex,
        getItemProps,
        selectedItem,
        clearSelection
      }) => (
        <div>
          <FieldLabel {...getLabelProps()}>Choose an Arp Media</FieldLabel>
          <Box>
            <Flex css={{ gap: '$16' }}>
              {/* @ts-ignore */}
              <TextInput
                {...getInputProps({ onChange: clearSelection })}
                placeholder="Search arp media"
                css={{ maxWidth: '100%', width: '400px' }}
              />
              {selectedItem && (
                <Flex css={{ alignItems: 'center' }}>
                  <IconButton
                    onClick={(e) => {
                      e.preventDefault()
                      clearSelection()
                      onChange(undefined)
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Flex>
              )}
            </Flex>
          </Box>
          <StyledMenuSelect {...getMenuProps()}>
            {isOpen &&
              items.map((item, index) => {
                const isSelected = selectedItem?.id === item.id

                return (
                  <StyledMenuSelectItem
                    key={item.id}
                    {...getItemProps({ item, index })}
                    highlighted={highlightedIndex === index}
                    selected={isSelected}
                  >
                    <Text
                      size="14"
                      css={{ marginRight: '8px', fontWeight: isSelected ? '$bold' : 'initial' }}
                    >
                      {item.metadata.title}
                    </Text>
                    <Text size="10" type="secondary">
                      {' '}
                      ({item.id})
                    </Text>
                  </StyledMenuSelectItem>
                )
              })}
          </StyledMenuSelect>
          {selectedItem && (
            <Flex css={{ gap: '$16', marginTop: '$10' }}>
              <Avatar imageUrl={selectedItem.user.profilePicture?.url} />
              <Flex direction="column">
                <Flex css={{ gap: '4px', alignItems: 'center' }}>
                  <Text size="12" css={{ color: '$gray08' }}>
                    ({selectedItem.id})
                  </Text>
                  <Text size="16">{selectedItem.metadata.title}</Text>
                  <Badge>{selectedItem.type}</Badge>
                </Flex>
                <Text size="12" css={{ color: '$gray08' }}>
                  {selectedItem.user.displayName}
                </Text>
              </Flex>
            </Flex>
          )}
          <FieldErrorLabel>{errorMessage}</FieldErrorLabel>
        </div>
      )}
    </Downshift>
  )
}
